import React, { useState,useEffect } from "react";
import { Card, Form, Row, Col, Input, Select, InputNumber, Upload, Image, Button } from "antd";
import { FileTextOutlined, UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import useUsers from "../../hooks/users/useUsers";
import useOrgHierarchy from "../../hooks/projects/useOrgHierarchy";

const { Option } = Select;

const areaMapping = {
  "765 kV": { AIS: "36.00 ha", GIS: "12.25 Ha" },
  "400 kV": { AIS: "20.25 Ha", GIS: "4.00 ha" },
  "220 kV": { AIS: "6.25 Ha", GIS: "1.44 Ha" },
  "132 kV": { AIS: "2.25 Ha", GIS: "0.8 Ha" },
  "110 kV": { AIS: "2.25 Ha", GIS: "0.8 Ha" },
  "100 kV": { AIS: "2.25 Ha", GIS: "0.8 Ha" },
};

const VOLTAGE_OPTIONS = ["765 kV", "400 kV", "220 kV", "132 kV", "110 kV", "100 kV"];
const transformProjectData = (project) => {
  if (!project) return {};

  return {
    ...project,
    district: project.district_name || project.district,
    taluka: project.taluka_name || project.taluka,
    zone: project.zone_name || project.zone,
    civil_circle: project.circle_name || project.civil_circle,
    civil_division: project.div_name || project.civil_division,
    subdivision_office: project.subdiv_name || project.subdivision_office,

    image: project.image
      ? [
          {
            uid: "-1",
            name: "current-image.jpg",
            status: "done",
            url: project.image,
          },
        ]
      : [],

    voltage: project.voltage,
  };
};

function CommonDataForm({ form, project }) {

  // Form watches for cascading
  const selectedZone = Form.useWatch("zone", form);
  const selectedDistrict = Form.useWatch("district", form);
  const imageFileList = Form.useWatch("image", form) || [];
  const selectedCircle = Form.useWatch("civil_circle", form);
  const selectedDivision = Form.useWatch("civil_division", form);
  const [previewUrl, setPreviewUrl] = useState("");
    const {
      zonesList,
      circlesList,
      districtsList,
      talukasList,
      divisionsList,
      subdivisionsList,
      loading: hierarchyLoading,
    } = useOrgHierarchy();
    const {
      users,
      loading: usersLoading,
      fetchUsers,
    } = useUsers();
      useEffect(() => {
        fetchUsers({
            page_size: 1000, // or a sufficiently large number
          });
        }, [fetchUsers]);

    useEffect(() => {

      if (!imageFileList.length) {
        setPreviewUrl("");
        return;
      }
      const file = imageFileList[0];
      if (file.url) {
        setPreviewUrl(file.url);  
        return;
      }

      if (file.originFileObj) {
        const objectUrl = URL.createObjectURL(file.originFileObj);
        setPreviewUrl(objectUrl);
      }
    }, [imageFileList]);

    useEffect(() => {
      if (project) {
        console.log("Project:", project);
        console.log("project.image:", project.image);

        const data = transformProjectData(project);
        console.log("Transformed:", data);

        form.setFieldsValue(data);
        console.log("Image in form:", form.getFieldValue("image"));
      }
    }, [project, form]);


  const applyAreaMapping = (voltage, technology) => {
    const area = areaMapping[voltage]?.[technology];
    if (area) form.setFieldsValue({ land_area_required: area });
  };

  // Filtered options
  const filteredDistricts = districtsList.filter(
    (d) => !selectedZone || d.zone_name === selectedZone
  );
  const filteredTalukas = talukasList.filter(
    (t) => !selectedDistrict || t.district_name === selectedDistrict
  );
  const filteredCircles = circlesList.filter(
    (c) => !selectedZone || c.zone_name === selectedZone
  );

  const filteredDivisions = divisionsList.filter(
      d => !selectedCircle || d.circle_name === selectedCircle
  );

  const filteredSubdivisions = subdivisionsList.filter(
      s => !selectedDivision || s.div_name === selectedDivision
  );

  // Reset dependent fields
  const handleZoneChange = () => {
    form.setFieldsValue({
      district: null,
      taluka: null,
      civil_circle: null,
      civil_division: null,
      subdivision_office: null,
    });
  };

  const handleDistrictChange = () => {
    form.setFieldsValue({ taluka: null });
  };

  const handleCircleChange = () => {
    form.setFieldsValue({ civil_division: null, subdivision_office: null });
  };
  const handleDivisionChange = () => {
    form.setFieldsValue({
      subdivision_office: null,
    });
  };

  return (
    <Card
      className="scheme-section-card"
      title={
        <div className="card-title-with-icon">
          <FileTextOutlined style={{ color: "#da241c", fontSize: "18px" }} />
          <span>Substation Basic Information</span>
        </div>
      }
    >
      <Form layout="vertical" form={form}>
        <Row gutter={[20, 16]}>
          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Name of Substation" name="name" rules={[{ required: true }]}>
              <Input placeholder="Enter substation name" size="large" />
            </Form.Item>
          </Col>

          {/* Project Image */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              label="Project Image"
              name="image"
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList) || []}
            >
              <Upload
                listType="picture-card"
                beforeUpload={() => false}
                maxCount={1}
                fileList={imageFileList}
                showUploadList={true}
              >
                {imageFileList.length === 0 && (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Upload New Image</div>
                  </div>
                )}
              </Upload>
            </Form.Item>

            {imageFileList.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <Image
                    width={180}
                    src={previewUrl}
                    alt="Project Preview"
                />
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  style={{ marginTop: 8 }}
                  onClick={() => form.setFieldsValue({ image: [] })}
                >
                  Remove Image
                </Button>
              </div>
            )}
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Latitude" name="latitude" rules={[{ required: true }]}>
              <InputNumber placeholder="19.65" size="large" style={{ width: "100%" }} step={0.000001} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Longitude" name="longitude" rules={[{ required: true }]}>
              <InputNumber placeholder="17.25" size="large" style={{ width: "100%" }} step={0.000001} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="STU Plan Year" name="stu_year">
              <Input size="large" />
            </Form.Item>
          </Col>

          {/* Zone */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Zone" name="zone">
              <Select
                placeholder="Select Zone"
                size="large"
                allowClear
                loading={hierarchyLoading}
                showSearch
                optionFilterProp="children"
                onChange={handleZoneChange}
              >
                {zonesList.map((zone) => (
                  <Option key={zone.code} value={zone.name}>
                    {zone.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* District */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item label="District" name="district">
              <Select
                placeholder="Select District"
                size="large"
                allowClear
                loading={hierarchyLoading}
                showSearch
                optionFilterProp="children"
                onChange={handleDistrictChange}
              >
                {filteredDistricts.map((dist, idx) => (
                  <Option key={idx} value={dist.district_name}>
                    {dist.district_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Taluka */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Taluka" name="taluka">
              <Select
                placeholder="Select Taluka"
                size="large"
                allowClear
                loading={hierarchyLoading}
                showSearch
                optionFilterProp="children"
              >
                {filteredTalukas.map((taluka, idx) => (
                  <Option key={idx} value={taluka.taluka_name}>
                    {taluka.taluka_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Village" name="village">
              <Input size="large" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Land Type" name="land_type" rules={[{ required: true }]}>
              <Select size="large">
                <Option value="GOVT">Government</Option>
                <Option value="MIDC">MIDC</Option>
                <Option value="PRIVATE">Private</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Voltage Level" name="voltage" rules={[{ required: true }]}>
              <Select
                placeholder="Select Voltage Level"
                size="large"
                allowClear
                onChange={(val) => applyAreaMapping(val, form.getFieldValue("technology"))}
              >
                {VOLTAGE_OPTIONS.map((v) => (
                  <Option key={v} value={v}>
                    {v}
                  </Option>
                ))}
                {project?.voltage && !VOLTAGE_OPTIONS.includes(project.voltage) && (
                  <Option value={project.voltage}>{project.voltage} (Legacy)</Option>
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Technology" name="technology" rules={[{ required: true }]}>
              <Select
                size="large"
                allowClear
                onChange={(val) => applyAreaMapping(form.getFieldValue("voltage"), val)}
              >
                <Option value="AIS">AIS</Option>
                <Option value="GIS">GIS</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Land Area Required (Ha)" name="land_area_required">
              <Input size="large" readOnly />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Project Initiation Date" name="assignment_start_date">
              <Input type="date" size="large" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Project Status" name="status">
              <Select size="large">
                <Option value="UNIDENTIFIED">Unidentified</Option>
                <Option value="IN_PROGRESS">In Progress</Option>
                <Option value="COMPLETED">Completed</Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Civil Circle */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Civil Circle" name="civil_circle">
              <Select
                placeholder="Select Civil Circle"
                size="large"
                allowClear
                loading={hierarchyLoading}
                showSearch
                optionFilterProp="children"
                onChange={handleCircleChange}
              >
                  {filteredCircles.map((circle, idx) => (
                  <Option key={idx} value={circle.circle_name}>
                    {circle.circle_name}
                  </Option>
                ))}
               
              </Select>
            </Form.Item>
          </Col>

          {/* Civil Division */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Civil Division" name="civil_division">
              <Select
                placeholder="Select Civil Division"
                size="large"
                 onChange={handleDivisionChange}
                allowClear
                loading={hierarchyLoading}
                showSearch
                optionFilterProp="children"
              >
                 {filteredDivisions.map((division) => (
                  <Option
                    key={division.div_code}
                    value={division.div_name}
                  >
                    {division.div_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item
                  label="Assigned Engineer"
                  name="assigned_engineer"
              >
                  <Select
                      placeholder="Select Engineer"
                      size="large"
                      loading={usersLoading}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                          option?.label
                              ?.toLowerCase()
                              .includes(input.toLowerCase())
                      }
                  >
                      {users.map(user => (
                          <Option
                              key={user.id}
                              value={user.id}
                              label={`${user.first_name} ${user.last_name}`}
                          >
                              {user.first_name} {user.last_name}
                          </Option>
                      ))}
                  </Select>
              </Form.Item>
          </Col>

          {/* Subdivision Office */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Subdivision Office" name="subdivision_office">
              <Select
                placeholder="Select Subdivision Office"
                size="large"
                allowClear
                loading={hierarchyLoading}
                showSearch
                optionFilterProp="children"
              >
                {filteredSubdivisions.map((subdivision) => (
                    <Option
                      key={subdivision.subdiv_code}
                      value={subdivision.subdiv_name}
                    >
                      {subdivision.subdiv_name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Subdivision Officer Name" name="subdivision_officer_name">
              <Input size="large" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Contact Number" name="sdo_contact">
              <Input size="large" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={8}>
            <Form.Item label="Email Address" name="sdo_email">
              <Input type="email" size="large" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
}

export default CommonDataForm;